import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useBilling } from "@/contexts/BillingContext";
import { toast } from "sonner";
import { Sparkles, Crown, ShoppingCart, CheckCircle2, Loader2, AlertTriangle, Receipt, FileText, AlertCircle } from "lucide-react";

const Billing = () => {
    const { billing, refresh } = useBilling();
    const [params, setParams] = useSearchParams();
    const [loadingPkg, setLoadingPkg] = useState(null);
    const [polling, setPolling] = useState(false);
    const [pollMessage, setPollMessage] = useState("");
    const [transactions, setTransactions] = useState([]);
    const [info, setInfo] = useState(null);
    const [infoModal, setInfoModal] = useState(false);
    const [pendingPkg, setPendingPkg] = useState(null);
    const navigate = useNavigate();

    const loadInfo = async () => {
        try { const { data } = await api.get("/billing/info"); setInfo(data); }
        catch (_e) { /* */ }
    };
    useEffect(() => { loadInfo(); }, []);

    const loadTx = async () => {
        try {
            const { data } = await api.get("/billing/transactions");
            setTransactions(data);
        } catch (_e) { /* */ }
    };
    useEffect(() => { loadTx(); }, []);

    useEffect(() => {
        const sid = params.get("session_id");
        if (sid) {
            setPolling(true);
            setPollMessage("Verifico il pagamento...");
            let attempts = 0;
            const max = 8;
            const poll = async () => {
                attempts++;
                try {
                    const { data } = await api.get(`/billing/checkout-status/${sid}`);
                    if (data.payment_status === "paid") {
                        setPollMessage("✓ Pagamento confermato");
                        toast.success("Pagamento confermato");
                        await refresh();
                        await loadTx();
                        setPolling(false);
                        params.delete("session_id");
                        setParams(params, { replace: true });
                        return;
                    }
                    if (data.status === "expired" || attempts >= max) {
                        setPollMessage("Pagamento non completato o scaduto.");
                        toast.error("Pagamento non completato");
                        setPolling(false);
                        return;
                    }
                    setPollMessage(`In elaborazione... (${attempts}/${max})`);
                    setTimeout(poll, 2000);
                } catch (e) {
                    setPollMessage(formatError(e));
                    setPolling(false);
                }
            };
            poll();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buy = async (packageId) => {
        const isSubscription = packageId.startsWith("subscription");
        if (isSubscription && (!info || !info.complete)) {
            setPendingPkg(packageId);
            setInfoModal(true);
            toast.info("Compila i dati di fatturazione per proseguire");
            return;
        }
        setLoadingPkg(packageId);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/billing/checkout", { package_id: packageId, origin_url: origin });
            window.location.href = data.url;
        } catch (e) {
            if (e?.response?.status === 412) {
                setPendingPkg(packageId);
                setInfoModal(true);
                toast.error(e.response.data.detail);
            } else {
                toast.error(formatError(e));
            }
            setLoadingPkg(null);
        }
    };

    const onInfoSaved = async (saved) => {
        setInfo(saved);
        setInfoModal(false);
        if (pendingPkg) {
            const pkg = pendingPkg;
            setPendingPkg(null);
            // proceed automatically with checkout
            setLoadingPkg(pkg);
            try {
                const origin = window.location.origin;
                const { data } = await api.post("/billing/checkout", { package_id: pkg, origin_url: origin });
                window.location.href = data.url;
            } catch (e) {
                toast.error(formatError(e));
                setLoadingPkg(null);
            }
        }
    };

    if (!billing) return <div className="text-elegant">Caricamento...</div>;

    const subActive = billing.subscription_active;
    const expDate = billing.subscription_expires_at ? new Date(billing.subscription_expires_at) : null;

    return (
        <div className="space-y-6 max-w-5xl" data-testid="billing-page">
            <div>
                <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Abbonamento</div>
                <h1 className="font-display text-4xl font-bold text-warm">Piano & Pacchetti AI</h1>
                <p className="text-elegant mt-2 max-w-2xl">
                    Un solo abbonamento mensile sblocca tavoli e utenti illimitati.
                    Le richieste AI hanno 10 generazioni gratuite ogni mese; pacchetti aggiuntivi acquistati non scadono mai.
                </p>
            </div>

            {polling && (
                <div className="bg-brand-soft border border-brand/40 rounded-lg p-4 flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-brand animate-spin" />
                    <div className="text-sm text-warm">{pollMessage}</div>
                </div>
            )}

            {billing.stripe_mode === "test" && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-3 text-xs text-amber-300 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Modalità <strong>TEST</strong> Stripe attiva — i pagamenti non sono reali. Imposta <code className="bg-deep px-1 rounded">STRIPE_MODE=live</code> in backend/.env per attivare la chiave LIVE.
                </div>
            )}

            {/* Dati di fatturazione — obbligatori prima dell'abbonamento */}
            <BillingInfoCard info={info} onEdit={() => setInfoModal(true)} />

            {billing.trial_discount_available && (
                <div className="bg-gradient-to-r from-brand/20 to-brand/5 border-2 border-brand/60 rounded-lg p-5 flex items-center justify-between flex-wrap gap-4" data-testid="trial-discount-banner">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center text-deep font-display font-bold text-xl">-20%</div>
                        <div>
                            <div className="font-display text-xl font-bold text-warm">Offerta Trial</div>
                            <div className="text-sm text-elegant">
                                Attiva Premium adesso a <strong className="text-brand">€{billing.trial_discount_price_eur.toFixed(2)}</strong> invece di €{billing.subscription_price_eur} (primo mese).
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => buy("subscription_trial_discount")}
                        disabled={loadingPkg === "subscription_trial_discount"}
                        data-testid="buy-trial-discount-button"
                        className="h-12 px-6 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-wider rounded-md disabled:opacity-60 flex items-center gap-2"
                    >
                        {loadingPkg === "subscription_trial_discount" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                        Riscatta sconto
                    </button>
                </div>
            )}

            {/* Current Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-5" data-testid="status-subscription">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand">{billing.is_trial ? "Trial Gratuito" : "Abbonamento"}</div>
                        <Crown className={`w-5 h-5 ${subActive ? "text-brand" : "text-elegant"}`} />
                    </div>
                    <div className="font-display text-2xl font-bold">
                        {subActive ? (billing.is_trial ? `${billing.days_remaining} giorni` : "Attivo") : (billing.trial_used ? "Trial scaduto" : "Non attivo")}
                    </div>
                    {expDate && (
                        <div className="text-xs text-elegant mt-1">
                            {subActive ? "Scade il" : "Scaduto il"} {expDate.toLocaleDateString("it-IT")}
                        </div>
                    )}
                    {billing.is_trial && subActive && (
                        <div className="mt-2 text-[10px] uppercase tracking-wider bg-brand/15 text-brand px-2 py-1 rounded inline-block">10 giorni gratis</div>
                    )}
                </div>
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand">AI Gratuite</div>
                        <Sparkles className="w-5 h-5 text-brand" />
                    </div>
                    <div className="font-display text-2xl font-bold" data-testid="ai-free-remaining">
                        {billing.ai_free_remaining} <span className="text-elegant text-base">/ {billing.ai_free_per_month}</span>
                    </div>
                    <div className="text-xs text-elegant mt-1">Reset il 1° del mese</div>
                </div>
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-5">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand">AI Acquistate</div>
                        <ShoppingCart className="w-5 h-5 text-brand" />
                    </div>
                    <div className="font-display text-2xl font-bold" data-testid="ai-purchased-remaining">{billing.ai_purchased_remaining}</div>
                    <div className="text-xs text-elegant mt-1">Non scadono mai</div>
                </div>
            </div>

            {/* Plans */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`relative bg-surface border rounded-lg p-6 ${subActive ? "border-brand" : "border-[#1f2c44]"}`}>
                    <div className="absolute top-0 right-0 bg-brand text-deep text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-lg rounded-tr-lg">Più scelto</div>
                    <Crown className="w-8 h-8 text-brand mb-3" />
                    <div className="font-display text-2xl font-bold">Smart Table Premium</div>
                    <div className="text-elegant text-sm mt-1">Mensile · Tutte le funzionalità</div>
                    <div className="font-display text-5xl font-bold mt-6">
                        € {billing.subscription_price_eur} <span className="text-base font-normal text-elegant">/mese</span>
                    </div>
                    <ul className="space-y-2 mt-6 text-sm">
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Tavoli illimitati</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Utenti illimitati</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Sincronizzazione renova360</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Stampanti telematiche</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Realtime cucina &amp; cassa</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> 10 Insights AI gratuite/mese</li>
                    </ul>
                    <button
                        onClick={() => buy("subscription_monthly")}
                        disabled={loadingPkg === "subscription_monthly"}
                        data-testid="buy-subscription-button"
                        className="w-full h-14 mt-6 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-wider rounded-md transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                        {loadingPkg === "subscription_monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                        {subActive ? "Rinnova ora" : "Attiva ora"}
                    </button>
                </div>

                <div className="bg-surface border border-[#1f2c44] rounded-lg p-6">
                    <Sparkles className="w-8 h-8 text-brand mb-3" />
                    <div className="font-display text-2xl font-bold">Pacchetto Insights AI</div>
                    <div className="text-elegant text-sm mt-1">50 richieste · Mai scadenza</div>
                    <div className="font-display text-5xl font-bold mt-6">
                        € {billing.ai_pack_price_eur}
                    </div>
                    <ul className="space-y-2 mt-6 text-sm">
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> {billing.ai_pack_requests} richieste AI</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Non scadono mai</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Si sommano alle 10 gratuite mensili</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Claude Sonnet 4.5</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" /> Storia completa salvata</li>
                    </ul>
                    <button
                        onClick={() => buy("ai_pack_50")}
                        disabled={loadingPkg === "ai_pack_50"}
                        data-testid="buy-ai-pack-button"
                        className="w-full h-14 mt-6 border-2 border-brand text-brand hover:bg-brand-soft font-display font-bold uppercase tracking-wider rounded-md transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                        {loadingPkg === "ai_pack_50" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                        Acquista pacchetto
                    </button>
                </div>
            </div>

            {/* Transactions */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-[#1f2c44] flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-brand" />
                    <div className="font-display font-bold">Storico Pagamenti</div>
                </div>
                {transactions.length === 0 ? (
                    <div className="p-6 text-center text-elegant text-sm">Nessuna transazione.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-surface-elevated">
                            <tr className="text-left text-elegant text-xs uppercase tracking-wider">
                                <th className="px-4 py-3">Data</th>
                                <th className="px-4 py-3">Pacchetto</th>
                                <th className="px-4 py-3">Importo</th>
                                <th className="px-4 py-3">Stato</th>
                                <th className="px-4 py-3">Modalità</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((t) => (
                                <tr key={t.id} className="border-t border-[#1f2c44]">
                                    <td className="px-4 py-3 text-elegant">{new Date(t.created_at).toLocaleString("it-IT")}</td>
                                    <td className="px-4 py-3">{t.metadata?.package_id === "subscription_monthly" ? "Abbonamento Premium" : "Pacchetto AI 50"}</td>
                                    <td className="px-4 py-3 font-display font-bold">€ {t.amount.toFixed(2)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-full ${
                                            t.payment_status === "paid" ? "bg-brand-soft text-brand" :
                                            t.payment_status === "pending" ? "bg-amber-900/30 text-amber-400" :
                                            "bg-red-900/30 text-red-400"
                                        }`}>{t.payment_status}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-elegant">{t.stripe_mode}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {infoModal && (
                <BillingInfoModal
                    initial={info}
                    pendingPkg={pendingPkg}
                    onClose={() => { setInfoModal(false); setPendingPkg(null); }}
                    onSaved={onInfoSaved}
                />
            )}
        </div>
    );
};

// ---------- BillingInfoCard ----------
const BillingInfoCard = ({ info, onEdit }) => {
    const complete = info?.complete;
    return (
        <div
            data-testid="billing-info-card"
            className={`rounded-lg p-5 flex items-start gap-4 flex-wrap ${
                complete
                    ? "bg-surface border border-[#1f2c44]"
                    : "bg-amber-900/15 border-2 border-amber-600/50"
            }`}
        >
            <div className={`w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0 ${complete ? "bg-brand-soft text-brand" : "bg-amber-900/40 text-amber-300"}`}>
                {complete ? <FileText className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-[260px]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-elegant mb-1">Dati di fatturazione</div>
                <div className="font-display text-xl font-bold text-warm" data-testid="billing-info-status">
                    {complete ? info.ragione_sociale : "Da completare"}
                </div>
                {complete ? (
                    <div className="text-xs text-elegant mt-1 space-y-0.5">
                        <div>{info.vat_or_cf} · {info.address}</div>
                        <div>{info.email} · {info.phone}</div>
                        <div>PEC/SDI: <span className="text-warm">{info.pec_or_sdi}</span></div>
                    </div>
                ) : (
                    <div className="text-sm text-amber-200 mt-1">
                        Per attivare l'abbonamento devi prima inserire i dati di fatturazione (Ragione sociale, P.IVA/CF, indirizzo, email, PEC/SDI, telefono).
                    </div>
                )}
            </div>
            <button
                onClick={onEdit}
                data-testid="edit-billing-info-button"
                className={`h-11 px-5 rounded-md text-xs uppercase tracking-wider font-bold ${
                    complete
                        ? "border border-brand text-brand hover:bg-brand-soft"
                        : "bg-amber-500 hover:bg-amber-400 text-deep"
                }`}>
                {complete ? "Modifica" : "Compila ora"}
            </button>
        </div>
    );
};

// ---------- BillingInfoModal ----------
const BillingInfoModal = ({ initial, pendingPkg, onClose, onSaved }) => {
    const [form, setForm] = useState({
        ragione_sociale: initial?.ragione_sociale || "",
        vat_or_cf: initial?.vat_or_cf || "",
        address: initial?.address || "",
        email: initial?.email || "",
        pec_or_sdi: initial?.pec_or_sdi || "",
        phone: initial?.phone || "",
    });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/billing/info", form);
            toast.success("Dati di fatturazione salvati");
            onSaved(data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="billing-info-modal"
            >
                <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-6 h-6 text-brand" />
                    <h2 className="font-display text-2xl font-bold">Dati di fatturazione</h2>
                </div>
                <p className="text-sm text-elegant mb-5">
                    Questi dati saranno usati per emettere la fattura del tuo abbonamento. Sono obbligatori prima dell'attivazione.
                    {pendingPkg && <span className="block text-brand mt-1">→ Dopo il salvataggio procederai automaticamente al pagamento.</span>}
                </p>

                <div className="space-y-3">
                    <Field label="Ragione sociale / Nome e Cognome" required>
                        <input
                            value={form.ragione_sociale}
                            onChange={(e) => set("ragione_sociale", e.target.value)}
                            data-testid="bi-ragione-sociale"
                            placeholder="Es. MAKAI di Maria Laura Pellino"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    </Field>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="P.IVA / Codice Fiscale" required>
                            <input
                                value={form.vat_or_cf}
                                onChange={(e) => set("vat_or_cf", e.target.value)}
                                data-testid="bi-vat"
                                placeholder="Es. 09936410969"
                                className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none font-mono" />
                        </Field>
                        <Field label="Telefono" required>
                            <input
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                data-testid="bi-phone"
                                placeholder="Es. +39 333 1234567"
                                className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                        </Field>
                    </div>

                    <Field label="Indirizzo completo (via, civico, CAP, città)" required>
                        <input
                            value={form.address}
                            onChange={(e) => set("address", e.target.value)}
                            data-testid="bi-address"
                            placeholder="Es. Via Roma 12, 80100 Napoli (NA)"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    </Field>

                    <Field label="Email fatturazione" required>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            data-testid="bi-email"
                            placeholder="amministrazione@locale.it"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    </Field>

                    <Field label="PEC oppure Codice SDI" hint='Se non hai né PEC né Codice SDI lascia vuoto: verrà salvato "0000000".'>
                        <input
                            value={form.pec_or_sdi}
                            onChange={(e) => set("pec_or_sdi", e.target.value)}
                            data-testid="bi-pec-sdi"
                            placeholder="Es. fattura@pec.it oppure ABCDEF1"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    </Field>
                </div>

                <div className="flex gap-2 mt-6">
                    <button onClick={onClose} data-testid="bi-cancel"
                        className="flex-1 h-12 border border-[#1f2c44] rounded-md text-elegant">
                        Annulla
                    </button>
                    <button onClick={submit} disabled={saving} data-testid="bi-save"
                        className="flex-1 h-12 bg-brand text-deep font-bold rounded-md disabled:opacity-60 flex items-center justify-center gap-2">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {pendingPkg ? "Salva e procedi al pagamento" : "Salva"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, required, hint, children }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-wider text-elegant mb-1">
            {label}{required && <span className="text-amber-400"> *</span>}
        </label>
        {children}
        {hint && <div className="text-[11px] text-elegant mt-1">{hint}</div>}
    </div>
);

export default Billing;
